"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.renderWaypointToHtml = void 0;
const email_builder_1 = require("@usewaypoint/email-builder");
const emails_1 = require("../constants/emails");
const email_font_family_1 = require("./email-font-family");
const outlookEmailSafety_1 = require("./outlookEmailSafety");
const COLUMNS_CONTAINER_TABLE_REGEX = /<table[^>]*style="table-layout:fixed;border-collapse:collapse"[^>]*>.*?<tr style="width:100%">/g;
const DEFAULT_SPACER_HEIGHT = 16;
const HEX_COLOR_REGEX = /^#[0-9a-f]{6}$/i;
const LAYOUT_ROOT_DIV_STYLE_REGEX = /<body[^>]*>\s*<div\s+style="/;
const BUTTON_SIZE_PADDING = {
    "x-small": [4, 8],
    small: [8, 12],
    medium: [12, 20],
    large: [16, 32],
};
const BUTTON_BORDER_RADIUS = { rectangle: undefined, pill: 64, rounded: 4 };
const DEFAULT_BUTTON_TEXT_COLOR = "#FFFFFF";
const DEFAULT_BUTTON_BACKGROUND_COLOR = "#999999";
const DEFAULT_BUTTON_FONT_SIZE = 16;
const escapeForReactMarkup = (value) => value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;");
const buildButtonAnchorHtml = (data, fontFamilyCss) => {
    const style = data.style;
    const props = data.props;
    const [paddingY, paddingX] = BUTTON_SIZE_PADDING[props?.size ?? "medium"];
    const borderRadius = BUTTON_BORDER_RADIUS[props?.buttonStyle ?? "rounded"];
    const anchorStyle = [
        `color:${props?.buttonTextColor ?? DEFAULT_BUTTON_TEXT_COLOR}`,
        `font-size:${style?.fontSize ?? DEFAULT_BUTTON_FONT_SIZE}px`,
        `font-family:${fontFamilyCss}`,
        `font-weight:${style?.fontWeight ?? "bold"}`,
        `background-color:${props?.buttonBackgroundColor ?? DEFAULT_BUTTON_BACKGROUND_COLOR}`,
        ...(borderRadius === undefined ? [] : [`border-radius:${borderRadius}px`]),
        `display:${props?.fullWidth ? "block" : "inline-block"}`,
        `padding:${paddingY}px ${paddingX}px`,
        "text-decoration:none",
    ].join(";");
    const buildMsoSpacer = (extraStyle) => `<span><!--[if mso]><i style="letter-spacing: ${paddingX}px;mso-font-width:-100%${extraStyle}" hidden>` +
        `&nbsp;</i><![endif]--></span>`;
    return (`<a href="${escapeForReactMarkup(props?.url ?? "")}" style="${escapeForReactMarkup(anchorStyle)}" target="_blank">` +
        buildMsoSpacer(`;mso-text-raise:${(paddingX * 2 * 3) / 4}`) +
        `<span>${escapeForReactMarkup(props?.text ?? "")}</span>` +
        buildMsoSpacer("") +
        "</a>");
};
const rewriteEmailSafeFontBlock = (block) => {
    if (block.type !== "Html" && block.type !== "Button") {
        return block;
    }
    const data = block.data;
    const fontFamily = data.style?.fontFamily;
    if (!(0, email_font_family_1.isEmailSafeFontFamily)(fontFamily)) {
        return block;
    }
    const fontFamilyCss = email_font_family_1.EMAIL_FONT_FAMILY_CSS[fontFamily];
    if (block.type === "Button") {
        return {
            type: "Html",
            data: {
                style: {
                    backgroundColor: data.style?.backgroundColor,
                    textAlign: data.style?.textAlign,
                    padding: data.style?.padding,
                },
                props: { contents: buildButtonAnchorHtml(block.data, fontFamilyCss) },
            },
        };
    }
    const htmlData = block.data;
    return {
        ...block,
        data: {
            ...htmlData,
            props: {
                ...htmlData.props,
                contents: `<div style="font-family:${escapeForReactMarkup(fontFamilyCss)}">` +
                    `${htmlData.props?.contents ?? ""}</div>`,
            },
        },
    };
};
const rewriteEmailSafeFontBlocks = (document) => {
    let rewrittenDocument = document;
    for (const [blockId, block] of Object.entries(document)) {
        const rewrittenBlock = rewriteEmailSafeFontBlock(block);
        if (rewrittenBlock === block) {
            continue;
        }
        if (rewrittenDocument === document) {
            rewrittenDocument = { ...document };
        }
        rewrittenDocument[blockId] = rewrittenBlock;
    }
    return rewrittenDocument;
};
const inlineLayoutEmailSafeFont = (html, document) => {
    const rootBlock = document[emails_1.WAYPOINT_ROOT_BLOCK_ID];
    const fontFamily = rootBlock?.type === "EmailLayout" ? rootBlock.data.fontFamily : undefined;
    if (!(0, email_font_family_1.isEmailSafeFontFamily)(fontFamily)) {
        return html;
    }
    const fontFamilyCss = escapeForReactMarkup(email_font_family_1.EMAIL_FONT_FAMILY_CSS[fontFamily]);
    return html.replace(LAYOUT_ROOT_DIV_STYLE_REGEX, (match) => `${match}font-family:${fontFamilyCss};`);
};
const rewriteSpacerBlock = (block) => {
    if (block.type !== "Spacer") {
        return block;
    }
    const spacerBlock = block;
    const backgroundColor = spacerBlock.data.style?.backgroundColor;
    if (!backgroundColor || !HEX_COLOR_REGEX.test(backgroundColor)) {
        return block;
    }
    const requestedHeight = spacerBlock.data.props?.height;
    const height = typeof requestedHeight === "number" && Number.isFinite(requestedHeight)
        ? Math.round(Math.max(0, requestedHeight))
        : DEFAULT_SPACER_HEIGHT;
    return {
        type: "Html",
        data: {
            style: { backgroundColor },
            props: {
                contents: `<div data-block-type="spacer" ` +
                    `style="background-color:${backgroundColor};height:${height}px;line-height:${height}px;font-size:1px">&nbsp;</div>`,
            },
        },
    };
};
const rewriteSpacerBlocks = (document) => {
    let rewrittenDocument = document;
    for (const [blockId, block] of Object.entries(document)) {
        const rewrittenBlock = rewriteSpacerBlock(block);
        if (rewrittenBlock === block) {
            continue;
        }
        if (rewrittenDocument === document) {
            rewrittenDocument = { ...document };
        }
        rewrittenDocument[blockId] = rewrittenBlock;
    }
    return rewrittenDocument;
};
/**
 * Gmail web/app, Apple Mail, iOS Mail, Outlook web/app, Yahoo, AOL read <style> blocks.
 * Outlook desktop ignores <style> and uses the MSO ghost table (desktop layout).
 * Gmailified accounts (3rd-party accounts checked via Gmail) strip <style> — they
 * fall back to the inline desktop layout, matching what they'd see on desktop web.
 *
 * gap vars to add columns gap inbetween vertically stacked columns on mobile
 * `margin-bottom: 16px` declaration acts as a fallback for Gmail's mobile app
 */
const RESPONSIVE_COLUMNS_STYLE = `<style type="text/css">
  @media only screen and (max-width: 480px) {
    .ebc-cols > .ebc-col {
      display: block !important;
      width: 100% !important;
      margin-left: auto !important;
      margin-right: auto !important;
      margin-bottom: 16px !important;
      margin-bottom: var(--ebc-gap, 16px) !important;
    }
    .ebc-cols > .ebc-col:last-child {
      margin-bottom: 0 !important;
    }
    .ebc-cols-2 > .ebc-col { max-width: 100% !important; }
    .ebc-cols-3 > .ebc-col { max-width: 66% !important; }
  }
</style>`;
/**
 * Parses `vertical-align` (top | middle | bottom) from a <td>'s style string.
 * Falls back to Waypoint's default `middle` when missing or unparseable.
 */
const parseVerticalAlign = (tdStyle) => {
    const match = tdStyle.match(/vertical-align\s*:\s*(top|middle|bottom)/);
    if (!match) {
        return "middle";
    }
    return match[1];
};
/**
 * Extracts each <td>'s innerHTML from inside a ColumnsContainer <tr>, along with
 * the container-level vertical-align and the first <td>'s padding-right to calculate the columns gap
 * Inner <table> blocks (e.g. Socials, Video) are skipped entirely so their
 * nested <td>/<tr> tags don't interfere with column boundary detection.
 * example columns container html:
 * <table style="table-layout:fixed;border-collapse:collapse"><tr style="width:100%"><td>content</td></tr></table>
 */
const extractColumnContents = (html, trStartIndex) => {
    const firstTdStart = html.indexOf("<td", trStartIndex);
    const firstTdTagEnd = html.indexOf(">", firstTdStart);
    const firstTdStyle = html.slice(firstTdStart, firstTdTagEnd);
    const verticalAlign = parseVerticalAlign(firstTdStyle);
    const firstTdPaddingRight = Number(firstTdStyle.match(/padding-right\s*:\s*([\d.]+)/)?.[1]) || 0;
    const columns = [];
    let pos = trStartIndex;
    let contentStart = -1;
    while (pos < html.length && !html.startsWith("</tr>", pos)) {
        if (html.startsWith("<table", pos)) {
            pos = html.indexOf("</table>", pos) + "</table>".length;
        }
        else if (html.startsWith("<td", pos)) {
            contentStart = html.indexOf(">", pos) + 1;
            pos = contentStart;
        }
        else if (html.startsWith("</td>", pos)) {
            columns.push(html.slice(contentStart, pos));
            pos += 5;
        }
        else {
            pos++;
        }
    }
    const trEndIndex = html.indexOf("</tr>", trStartIndex);
    const tbodyEndIndex = html.indexOf("</tbody>", trEndIndex);
    const tableEndIndex = html.indexOf("</table>", tbodyEndIndex);
    return {
        columns,
        verticalAlign,
        firstTdPaddingRight,
        endIndex: tableEndIndex + "</table>".length,
    };
};
/**
 * Reads the wrapper <div>'s style and returns its horizontal padding, so the caller
 * can subtract it from the 600px canvas when computing column widths.
 */
const parseContainerStyle = (html, tableStartIndex) => {
    const divOpenStart = html.lastIndexOf("<div", tableStartIndex);
    const divStyleMatch = html.slice(divOpenStart, tableStartIndex).match(/style="([^"]*)"/);
    const divStyle = divStyleMatch?.[1] ?? "";
    const { left: paddingLeft, right: paddingRight } = (0, outlookEmailSafety_1.parsePadding)(divStyle);
    return { divOpenStart, divStyle, paddingLeft, paddingRight };
};
/**
 * Builds the responsive HTML for a single ColumnsContainer:
 * inline-block <div>s for modern email clients + MSO ghost table for Outlook.
 * Default (inline) styling = desktop side-by-side layout.
 * Mobile stacked layout is applied via the media query in RESPONSIVE_COLUMNS_STYLE,
 */
const buildResponsiveColumnsHtml = (divStyle, columns, verticalAlign, columnWidth, columnsGap, columnsCount) => {
    const style = divStyle ? `${divStyle};` : "";
    const columnDivs = columns
        .map((content, index) => {
        const needsGap = index < columns.length - 1 && columnsGap > 0;
        const msoSeparator = index > 0 ? "<!--[if mso]></td><![endif]-->" : "";
        const msoPaddingRight = needsGap ? ` style="padding-right:${columnsGap}px"` : "";
        // horizontal gap for desktop side-by-side; mobile media query flips this to margin-bottom
        const marginRight = needsGap ? `margin-right:${columnsGap}px;` : "";
        return (msoSeparator +
            `<!--[if mso]><td width="${columnWidth}" valign="${verticalAlign}"${msoPaddingRight}><![endif]-->` +
            `<div class="ebc-col" style="display:inline-block;max-width:${columnWidth}px;width:100%;vertical-align:${verticalAlign};text-align:left;${marginRight}">` +
            content +
            "</div>");
    })
        .join("");
    return (`<div class="ebc-cols ebc-cols-${columnsCount}" style="${style}--ebc-gap:${columnsGap}px;text-align:center">` +
        '<!--[if mso]><table role="presentation" align="center" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><![endif]-->' +
        columnDivs +
        "<!--[if mso]></td></tr></table><![endif]-->" +
        "</div>");
};
/**
 * Fetches canvas border from legacy email bodies with borderColor option.
 * It is then taken into account when computing column widths so they render responsively correctly.
 */
const getCanvasHorizontalBorderWidth = (html) => {
    const canvasMaxWidthIndex = html.search(new RegExp(`max-width:\\s*${emails_1.EMAIL_CANVAS_MAX_WIDTH}px`));
    if (canvasMaxWidthIndex === -1) {
        return 0;
    }
    const styleStart = html.lastIndexOf('style="', canvasMaxWidthIndex);
    if (styleStart === -1) {
        return 0;
    }
    const styleEnd = html.indexOf('"', styleStart + 'style="'.length);
    const canvasStyle = html.slice(styleStart, styleEnd);
    const borderWidth = Number(canvasStyle.match(/border\s*:\s*([\d.]+)px\s+solid/)?.[1]) || 0;
    return borderWidth * 2;
};
/**
 * Recursively finds and rewrites each ColumnsContainer table into responsive markup.
 * Desktop layout is inlined (columns sit side-by-side within the 600px canvas);
 * mobile stacking + proportional widths are applied via RESPONSIVE_COLUMNS_STYLE.
 */
const rewriteColumnsContainersForResponsive = (html) => {
    COLUMNS_CONTAINER_TABLE_REGEX.lastIndex = 0;
    const canvasBorderWidth = getCanvasHorizontalBorderWidth(html);
    const processNextMatch = (fromIndex) => {
        COLUMNS_CONTAINER_TABLE_REGEX.lastIndex = fromIndex;
        // identify columns containers in email html and fetch them in array
        const match = COLUMNS_CONTAINER_TABLE_REGEX.exec(html);
        if (!match) {
            return html.slice(fromIndex);
        }
        const trStartIndex = match.index + match[0].length;
        const { divOpenStart, divStyle, paddingLeft, paddingRight } = parseContainerStyle(html, match.index);
        const { columns, verticalAlign, firstTdPaddingRight, endIndex: tableEndIndex, } = extractColumnContents(html, trStartIndex);
        const columnsCount = Math.min(Math.max(columns.length, emails_1.WAYPOINT_MIN_COLUMNS_COUNT), emails_1.WAYPOINT_MAX_COLUMNS_COUNT);
        // Waypoint distributes the user-configured gap as equal padding-right on each <td>;
        // invert that here to recover the original gap value.
        const columnsGap = columnsCount > 1 ? Math.round((firstTdPaddingRight * columnsCount) / (columnsCount - 1)) : 0;
        // Desktop: all columns + gaps must fit in the 600px canvas (minus canvas border and container padding).
        // Mobile proportional widths (2 → 100%, 3 → 66%) come from RESPONSIVE_COLUMNS_STYLE.
        const desktopContentWidth = emails_1.EMAIL_CANVAS_MAX_WIDTH - canvasBorderWidth - paddingLeft - paddingRight;
        const columnMaxWidth = Math.floor((desktopContentWidth - columnsGap * (columnsCount - 1)) / columnsCount);
        const responsiveHtml = buildResponsiveColumnsHtml(divStyle, columns, verticalAlign, columnMaxWidth, columnsGap, columnsCount);
        const precedingHtml = html.slice(fromIndex, divOpenStart);
        const nextIndex = tableEndIndex + "</div>".length;
        return precedingHtml + responsiveHtml + processNextMatch(nextIndex);
    };
    return processNextMatch(0);
};
/**
 * Renders a Waypoint document to HTML and applies post-processing:
 * - Inlines the CSS stack of email-safe font tokens Waypoint cannot resolve on its own
 * - Strips the body margin for "view in browser"
 * - Removes Waypoint's hardcoded 32px top/bottom padding on the email wrapper
 * - Fixes table layout so that long text is wrapped properly
 * - Rewrites ColumnsContainer tables into responsive inline-block columns with MSO fallback
 * - Injects the mobile responsive styling for ColumnsContainers into <head>
 * - Wraps the output in Outlook-Classic-Windows safety markup via `wrapForOutlook`
 *   (XHTML doctype, VML/Office namespaces, OfficeDocumentSettings, MSO `<style>` reset,
 *   VML `<v:roundrect>` buttons, widthless-img clamp, MSO ghost table).
 *   The wrap is idempotent (sentinel-guarded); legacy bodies persisted before this
 *   pipeline shipped get upgraded on the next save via `sanitizeWaypointSubmitPayload`,
 *   which re-renders through this function when the stored body is missing the Outlook wrap.
 */
const renderWaypointToHtml = (document) => {
    const renderableDocument = rewriteEmailSafeFontBlocks(rewriteSpacerBlocks(document));
    const rendered = (0, email_builder_1.renderToStaticMarkup)(renderableDocument, { rootBlockId: emails_1.WAYPOINT_ROOT_BLOCK_ID });
    const html = rewriteColumnsContainersForResponsive(inlineLayoutEmailSafeFont(rendered, document));
    const postProcessed = html
        .replace(/<\/head>/, `${RESPONSIVE_COLUMNS_STYLE}</head>`)
        .replace(/<body>/, '<body style="margin: 0;">')
        .replace(/padding:\s*32px\s+0/g, "padding: 0")
        .replace(/<table(\s[^>]*?)style="([^"]*)"([^>]*)>/, `<table$1style="$2; table-layout: fixed;"$3>`)
        .replace(/<td>/, `<td style="word-wrap: break-word; overflow-wrap: break-word; width: 100%;">`);
    return (0, outlookEmailSafety_1.wrapForOutlook)(postProcessed);
};
exports.renderWaypointToHtml = renderWaypointToHtml;
